/// <amd-module name="VSS/Features/MarkdownToolbar/Utilities" />

const CELL_SEPARATOR = "|";
const ROW_SEPARATOR = "\n";
const CELL_FILLER = " ";
const ALIGN_ROW_FILLER = "-";

export interface FormattedContent {
    tablesCount: number;
    content?: string;
    selectionStart?: number;
    selectionEnd?: number;
}

export function getTableString(rowCount: number, colCount: number): string {
    let rowString = CELL_SEPARATOR;
    let headerString = CELL_SEPARATOR;
    for (let i = 0; i < colCount; i++) {
        rowString += CELL_FILLER + CELL_FILLER + CELL_SEPARATOR;
        headerString += ALIGN_ROW_FILLER + ALIGN_ROW_FILLER + CELL_SEPARATOR;
    }

    let tableString = ROW_SEPARATOR + rowString + ROW_SEPARATOR + headerString + ROW_SEPARATOR;
    if (rowCount > 1) {
        for (let i = 1; i < rowCount; i++) {
            tableString += rowString + ROW_SEPARATOR;
        }
    }

    return tableString;
}

// returns the content with formatted tables, updated selection start and end index, if it finds any tables to format.
export function getFormattedContent(currentText: string, selectionStart: number, selectionEnd: number): FormattedContent {
    let tablesCount = 0;
    const originalLines = currentText.split(ROW_SEPARATOR);
    const modifiedLines = [];

    // to handle GB18030 (chinese) characters etc which occupy extra width.
    const canvasElement = document.createElement("CANVAS") as HTMLCanvasElement;
    const canvasContext = canvasElement.getContext && canvasElement.getContext("2d");
    if (canvasContext) {
        canvasContext.font = "12px Menlo, Consolas, Courier New, monospace"; // should be same as editor textArea's font
    }
    // exact character doesn't matter here. as all have same width in monospace font. using "a".
    const charWidth = canvasContext ? canvasContext.measureText("a").width : null;

    for (let i = 0; i < originalLines.length; i++) {
        // there should be minimum two lines for the content to be a table.
        if (i + 2 > originalLines.length) {
            modifiedLines.push(originalLines[i]);
            continue;
        }

        const hRow = originalLines[i];
        const alignRow = originalLines[i + 1];

        // there should be atleast one column separator in header and the next row should be a valid align row for it to be a valid table.
        if (hRow.indexOf(CELL_SEPARATOR) !== -1 && _isValidAlignRow(alignRow)) {
            tablesCount++;

            const formattedTable: string[][] = []; // to store the table cells.

            // use maxColumns to determine upto what column we need to loop over each column
            let maxColumns = _pushTableColumns(formattedTable, hRow);

            const alignColumnCount = _pushTableColumns(formattedTable, alignRow);
            maxColumns = alignColumnCount > maxColumns ? alignColumnCount : maxColumns;

            // find out the rest of the table rows.
            let j = i + 2;
            for (j = i + 2; j < originalLines.length; j++) {
                // no separator indicates, not part of the table. so break.
                if (originalLines[j].indexOf(CELL_SEPARATOR) === -1) {
                    break;
                }

                const columnCount = _pushTableColumns(formattedTable, originalLines[j]);
                maxColumns = columnCount > maxColumns ? columnCount : maxColumns;
            }

            // start formatting the table cells.

            // for first column (which is empty space at the beginning of the table), make sure only the first row's empty space is honored.
            const emptySpaceLength = formattedTable[0][0].length;
            formattedTable.forEach(row => {
                row[0] = _repeat(CELL_FILLER, emptySpaceLength);
            });

            // for remaining columns, find out the max width and append the extra spaces to remaining cells.
            for (let k = 1; k < maxColumns; k++) {
                let maxCellLength = 3; // keep the min cell length to 3 to work for center aligned scenario.
                formattedTable.forEach((row, index) => {
                    if (k < row.length) {
                        row[k] = row[k].trim();
                        if (index != 1 && k != row.length - 1) {
                            row[k] = CELL_FILLER + row[k] + CELL_FILLER; // empty space at start of every cell is needed (except for last column and align row), to honor syntax like mentions.
                        }
                        // do not consider the width of alignRow.
                        const cellLength = _stringLength(row[k], canvasContext, charWidth);
                        if (index != 1 && maxCellLength < cellLength) {
                            maxCellLength = cellLength;
                        }
                    }
                });

                formattedTable.forEach((row, index) => {
                    // check if current row has kth column, as all rows need not contain upto max columns
                    // also, don't do anything for last column
                    if (k < row.length - 1) {
                        // for alignRow, append `-` instead of spaces.
                        if (index === 1) {
                            const endIndex = row[k].length - 1;
                            // alignRows can start or end with `:`, so `-` should be appended in between instead.
                            if (row[k].length > 0) {
                                if (row[k].length < maxCellLength) {
                                    row[k] =
                                        row[k].slice(0, row[k].length - 1) +
                                        _repeat(ALIGN_ROW_FILLER, maxCellLength - row[k].length) +
                                        row[k][endIndex];
                                } else {
                                    row[k] = row[k].slice(0, maxCellLength - 1) + row[k][endIndex];
                                }
                            } else {
                                row[k] = _repeat(ALIGN_ROW_FILLER, maxCellLength);
                            }
                        } else {
                            // append extra white spaces for remaining cells.
                            row[k] += _repeat(CELL_FILLER, maxCellLength - _stringLength(row[k], canvasContext, charWidth));
                        }
                    }
                });
            }

            // update the modified lines with formatted tables.
            formattedTable.forEach(row => {
                modifiedLines.push(row.join(CELL_SEPARATOR));
            });

            // update the index to start processing remaining content.
            i = j - 1;
        } else {
            modifiedLines.push(originalLines[i]);
        }
    }

    if (tablesCount > 0) {
        // get the new selection start and end, incase any tables got formatted.
        const newSelectionStart = _getUpdatedIndex(originalLines, modifiedLines, selectionStart);
        const newSelectionEnd = _getUpdatedIndex(originalLines, modifiedLines, selectionEnd);
        return { tablesCount, content: modifiedLines.join(ROW_SEPARATOR), selectionStart: newSelectionStart, selectionEnd: newSelectionEnd };
    }

    return { tablesCount: 0 };
}

// finds the updatedIndex after replacing original lines with modified lines.
// Note: it's accurate upto the line number. Actual index within the line could be different depending on selected line is modified or not.
function _getUpdatedIndex(originalLines: string[], modifiedLines: string[], originalIndex: number): number {
    let processedIndex = 0;
    let lineNumber = 0;
    let selectionIndexInLine = 0;
    let updatedIndex = 0;
    for (let i = 0; i < originalLines.length; i++) {
        if (processedIndex + originalLines[i].length >= originalIndex) {
            lineNumber = i;
            selectionIndexInLine = originalIndex - processedIndex;
            break;
        }
        processedIndex += originalLines[i].length + 1; // +1 for newline character
    }

    for (let i = 0; i < lineNumber; i++) {
        updatedIndex += modifiedLines[i].length + 1;
    }
    updatedIndex += modifiedLines[lineNumber].length > selectionIndexInLine ? selectionIndexInLine : modifiedLines[lineNumber].length;

    return updatedIndex;
}

// inspired from the markdown-it's logic to determine a table
function _isValidAlignRow(row: string): boolean {
    // align row should not be empty string.
    if (!row.trim()) {
        return false;
    }
    // characters of the second line should contain only '|', '-', ':', spaces
    if (!/^[-:|\s]*$/.test(row)) {
        return false;
    }
    const columns = row.split(CELL_SEPARATOR);
    for (let i = 0; i < columns.length; i++) {
        const cell = columns[i].trim();
        if (!cell) {
            // allow empty columns before and after table, but not in between columns;
            // e.g. allow ` |---| `, disallow ` ---||--- `
            if (i === 0 || i === columns.length - 1) {
                continue;
            } else {
                return false;
            }
        }

        // each column should contain more than one and continuous `-`. Optionally can start or end with single `:`
        if (!/^:?-+:?$/.test(cell)) {
            return false;
        }
    }

    return true;
}

// splits the row into table columns and pushes them into the formattedTable.
// appends empty cells at start and end, depending on a `|` is present or not.
function _pushTableColumns(formattedTable: string[][], row: string): number {
    const columns = row.split(CELL_SEPARATOR);
    let columnCount = columns.length;
    const lastIndex = columns.length - 1;
    const emptyCell = [""];
    if (!columns[0].trim() && !columns[lastIndex].trim()) {
        columns[lastIndex] = ""; // if last cell contains only spaces, make it empty. //TODO
        formattedTable.push(columns);
    } else if (!columns[0].trim()) {
        formattedTable.push(columns.concat(emptyCell));
        columnCount += 1;
    } else if (!columns[lastIndex].trim()) {
        columns[lastIndex] = "";
        formattedTable.push(emptyCell.concat(columns));
        columnCount += 1;
    } else {
        formattedTable.push(emptyCell.concat(columns.concat(emptyCell)));
        columnCount += 2;
    }

    return columnCount;
}

function _repeat(toRepeat: string, times: number): string {
    let repeated = "";
    for (let i = 0; i < times; i++) {
        repeated += toRepeat;
    }
    return repeated;
}

// returns the visible length of a string taking characters with extra width(e.g: GB18030 characters) into consideration
function _stringLength(content: string, canvasContext: CanvasRenderingContext2D | null, charWidth: number | null): number {
    if (canvasContext && charWidth) {
        return Math.ceil(canvasContext.measureText(content).width / charWidth);
    }
    return content.length;
}
